import React from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Flex,
  Text,
  Box,
  Icon,
  useColorModeValue,
  Portal
} from '@chakra-ui/react';
import { FiGlobe, FiCheck } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { supportedLanguages, supportedLanguageOptions, changeLanguage, getCurrentLanguage } from '../../lib/i18n';
import { languageManager } from '../../services/languageManager';
import { getUserLanguagePreference } from '../../utils/languageUtils';
import { useLanguageDisplayNames } from '../../hooks/useLanguageDisplayNames';

type LanguageSwitcherProps = {
  variant?: 'icon' | 'full' | 'compact';
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'full',
  size = 'md',
  showLabel = true,
  placement = 'bottom'
}) => {
  const { t } = useTranslation(['v0/me', 'v0/common']);
  const currentLanguage = getCurrentLanguage();
  const currentUserPreference = getUserLanguagePreference() || 'system';
  const { getLanguageDisplayName } = useLanguageDisplayNames();

  const menuBg = useColorModeValue('white', 'gray.800');
  const menuHoverBg = useColorModeValue('gray.100', 'gray.700');
  const activeItemBg = useColorModeValue('teal.50', 'teal.900');
  const activeTextColor = useColorModeValue('teal.700', 'teal.200');

  const handleLanguageChange = async (lng: string) => {
    try {
      await languageManager.setUserLanguagePreference(lng);
    } catch (error) {
      console.error('Failed to set language preference:', error);
      // 回退到原来的方法
      changeLanguage(lng as keyof typeof supportedLanguages);
    }
  };
  
  const renderButton = () => {
    switch(variant) {
      case 'icon':
        return (
          <MenuButton
            as={Button}
            aria-label={t('v0/common:me.uiSettings.language')}
            leftIcon={<Icon as={FiGlobe} />}
            variant="ghost"
            size={size}
            borderRadius="full"
          >
            {showLabel && <Box ml={2}>{t('v0/common:me.uiSettings.language')}</Box>}
          </MenuButton>
        );
      
      case 'compact':
        return (
          <MenuButton
            as={Button}
            variant="ghost"
            size={size}
            rightIcon={<Icon as={FiGlobe} />}
            borderRadius="full"
          >
            {currentLanguage}
          </MenuButton>
        );
        
      case 'full':
      default:
        return (
          <MenuButton
            as={Button}
            variant="ghost"
            size={size}
            rightIcon={<Icon as={FiGlobe} />}
            borderRadius="full"
          >
            <Flex align="center">
              <Text>{t(`v0/common:me.uiSettings.languages.${currentLanguage}`)}</Text>
            </Flex>
          </MenuButton>
        );
    }
  };
  
  return (
    <Menu placement={placement}>
      {renderButton()}
      <Portal>
        <MenuList bg={menuBg} boxShadow="md" minW="180px" zIndex="popover">
          <Box px={3} py={2} fontWeight="medium" fontSize="sm" color="gray.500">
            {t('v0/common:me.uiSettings.languages.title')}
          </Box>
          
          {Object.entries(supportedLanguageOptions).map(([code, name]) => (
            <MenuItem
              key={code}
              onClick={() => handleLanguageChange(code)}
              bg={code === currentUserPreference ? activeItemBg : 'transparent'}
              color={code === currentUserPreference ? activeTextColor : 'inherit'}
              _hover={{ bg: menuHoverBg }}
            >
              <Flex justify="space-between" align="center" width="100%">
                <Text>{getLanguageDisplayName(code)}</Text>
                {code === currentUserPreference && (
                  <Icon as={FiCheck} ml={2} color={activeTextColor} />
                )}
              </Flex>
            </MenuItem>
          ))}
        </MenuList>
      </Portal>
    </Menu>
  );
};

export default LanguageSwitcher; 